'use client';

/**
 * Fork lineage visualization for a single prompt.
 *
 * Backend returns { ancestors[], root, descendants[] } — ancestors render
 * above the current prompt, descendants below. Each node is a clickable
 * pill with avatar + title; the CURRENT prompt is highlighted with an
 * accent ring so the user always knows where they are in the tree.
 *
 * Connector lines are rendered with pure CSS (border-left on a padded box)
 * — no SVG, no layout libs. Works at any zoom, stays sharp in all themes.
 */

import Link from 'next/link';
import { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { ForkTreeNode } from '@/types';

type TreeData = Awaited<ReturnType<typeof api.prompts.tree>>;

export default function ForkTree({ promptId }: { promptId: string }) {
  const [data, setData] = useState<TreeData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.prompts.tree(promptId)
      .then(setData)
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [promptId]);

  if (loading) {
    return <div style={{ padding: 20, color: 'var(--ink-3)', fontSize: 13 }}>Loading lineage…</div>;
  }
  if (!data) return null;

  const hasAny = data.ancestors.length > 0 || data.descendants.length > 0;
  if (!hasAny) {
    return (
      <div style={{
        padding: '28px 20px',
        background: 'var(--bg-soft)',
        border: '1px solid var(--line)',
        borderRadius: 'var(--radius-md)',
        color: 'var(--ink-3)', fontSize: 13, textAlign: 'center',
      }}>
        <span className="material-symbols-outlined" style={{ fontSize: 28, color: 'var(--ink-4)', display: 'block', marginBottom: 6 }}>
          fork_right
        </span>
        This prompt hasn&rsquo;t been remixed yet — and it&rsquo;s an original (no parent).
      </div>
    );
  }

  return (
    <div style={{
      padding: '20px 4px',
      fontSize: 13,
      color: 'var(--ink-2)',
    }}>
      {data.ancestors.length > 0 && (
        <div style={{ marginBottom: 6 }}>
          <Eyebrow>Ancestors</Eyebrow>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {data.ancestors.map((a, i) => (
              <div key={a.id} style={{ display: 'flex', alignItems: 'center', gap: 0 }}>
                <div style={{
                  width: 16 + i * 14, height: 18,
                  borderLeft: '1.5px solid var(--line)',
                  borderBottom: '1.5px solid var(--line)',
                  borderBottomLeftRadius: 8,
                  marginRight: 8,
                }} />
                <NodePill node={a} />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Current prompt — highlighted as "you are here". */}
      <div style={{
        margin: data.ancestors.length > 0 ? '6px 0 8px' : '0 0 8px',
        display: 'flex', alignItems: 'center', gap: 8,
        paddingLeft: 16 + Math.max(0, data.ancestors.length) * 14,
      }}>
        <NodePill
          node={{
            id: data.root.id,
            title: data.root.title,
            is_public: data.root.is_public,
            is_hidden: data.root.is_hidden,
            created_at: data.root.created_at,
            user: data.root.user,
          }}
          highlighted
        />
      </div>

      {data.descendants.length > 0 && (
        <div>
          <Eyebrow>Forks ({countDescendants(data.descendants)})</Eyebrow>
          <div style={{ paddingLeft: 16 + Math.max(0, data.ancestors.length) * 14 }}>
            <DescendantList nodes={data.descendants} depth={0} />
          </div>
        </div>
      )}

      {data.truncated && (
        <div style={{
          marginTop: 10, fontSize: 11, color: 'var(--ink-4)',
          fontStyle: 'italic',
        }}>
          Tree truncated at 120 nodes — there are more forks we couldn&rsquo;t show.
        </div>
      )}
    </div>
  );
}

function Eyebrow({ children }: { children: React.ReactNode }) {
  return (
    <div style={{
      fontSize: 10, fontWeight: 600, letterSpacing: '0.14em',
      textTransform: 'uppercase', color: 'var(--ink-4)',
      marginBottom: 8, paddingLeft: 4,
    }}>
      {children}
    </div>
  );
}

type TreeDescendant = ForkTreeNode & { children?: TreeDescendant[] };

function countDescendants(nodes: readonly TreeDescendant[]): number {
  let n = 0;
  const walk = (arr: readonly TreeDescendant[]) => {
    for (const c of arr) {
      n++;
      if (c.children?.length) walk(c.children);
    }
  };
  walk(nodes);
  return n;
}

function DescendantList({
  nodes,
  depth,
}: {
  nodes: (ForkTreeNode & { children: ForkTreeNode[] })[];
  depth: number;
}) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      {nodes.map((n) => (
        <div key={n.id}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 0 }}>
            <div style={{
              width: 18 + depth * 14, height: 18,
              borderLeft: '1.5px solid var(--line)',
              borderBottom: '1.5px solid var(--line)',
              borderBottomLeftRadius: 8,
              marginRight: 8,
            }} />
            <NodePill node={n} />
          </div>
          {n.children && n.children.length > 0 && (
            <DescendantList
              nodes={n.children as (ForkTreeNode & { children: ForkTreeNode[] })[]}
              depth={depth + 1}
            />
          )}
        </div>
      ))}
    </div>
  );
}

function NodePill({ node, highlighted = false }: { node: ForkTreeNode; highlighted?: boolean }) {
  const pill = (
    <div
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 8,
        padding: '6px 12px 6px 6px',
        background: highlighted ? 'var(--accent-ink, var(--accent))' : 'var(--bg-raised)',
        color: highlighted ? 'white' : 'var(--ink)',
        border: `1px solid ${highlighted ? 'var(--accent)' : 'var(--line)'}`,
        boxShadow: highlighted ? `0 0 0 2px color-mix(in oklch, var(--accent) 40%, transparent)` : 'none',
        borderRadius: 'var(--radius-full)',
        fontSize: 13,
        maxWidth: 340,
        transition: 'transform 0.08s, box-shadow 0.08s',
      }}
      className={highlighted ? '' : 'hover:-translate-y-[1px]'}
    >
      {node.user.avatar_url ? (
        // eslint-disable-next-line @next/next/no-img-element
        <img
          src={node.user.avatar_url}
          alt={node.user.name}
          style={{ width: 22, height: 22, borderRadius: '50%', objectFit: 'cover', flex: 'none' }}
        />
      ) : (
        <div style={{
          width: 22, height: 22, borderRadius: '50%', flex: 'none',
          display: 'grid', placeItems: 'center',
          background: highlighted ? 'rgba(255,255,255,0.2)' : 'var(--bg-soft)',
          color: highlighted ? 'white' : 'var(--ink-2)',
          fontSize: 9, fontWeight: 700, fontFamily: 'var(--font-serif)',
        }}>
          {node.user.name.slice(0, 2).toUpperCase()}
        </div>
      )}
      <span style={{
        fontFamily: 'var(--font-serif)', fontWeight: 500,
        letterSpacing: '-0.01em',
        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
      }}>
        {node.title}
      </span>
      {node.is_hidden && (
        <span className="material-symbols-outlined" style={{
          fontSize: 12, color: highlighted ? 'rgba(255,255,255,0.8)' : 'var(--ink-4)',
        }} title="Hidden by admin">
          visibility_off
        </span>
      )}
      {!node.is_public && !node.is_hidden && (
        <span className="material-symbols-outlined" style={{
          fontSize: 12, color: highlighted ? 'rgba(255,255,255,0.8)' : 'var(--ink-4)',
        }} title="Private">
          lock
        </span>
      )}
    </div>
  );
  // The current node shouldn't link back to itself.
  if (highlighted) return pill;
  return (
    <Link href={`/prompt/${node.id}`} className="no-underline">
      {pill}
    </Link>
  );
}
